import { apiRequest } from '@/lib/api/client';
import type { WorkshopManifest } from '@/lib/api/types';
import { useAsync, type AsyncState } from '@/lib/hooks/useAsync';

/**
 * Where the viewer reads workshop content from.
 *
 * The published platform serves a cohort-scoped, attendee-safe manifest built
 * server-side from the pinned source (US-2.2), which is why the manifest type
 * carries no repository or commit fields. Until that endpoint exists, the
 * viewer falls back to the placeholder manifest shipped with the app so it
 * still runs standalone — this repository never holds real workshop content.
 */
const FALLBACK_CONTENT_URL = '/content/sample-content.json';

async function loadFallback(signal: AbortSignal): Promise<WorkshopManifest> {
  const response = await fetch(FALLBACK_CONTENT_URL, { signal });
  if (!response.ok) throw new Error(`Could not load ${FALLBACK_CONTENT_URL}`);
  return (await response.json()) as WorkshopManifest;
}

export function useWorkshopContent(cohortId: string | null): {
  state: AsyncState<WorkshopManifest>;
  reload: () => void;
} {
  return useAsync<WorkshopManifest>(
    async (signal) => {
      if (!cohortId) return loadFallback(signal);
      return apiRequest<WorkshopManifest>(`/cohorts/${cohortId}/content`, { signal });
    },
    [cohortId],
  );
}
